import * as THREE from 'three';
import { STARTING_GRID } from '../track.js';
import { FLOW_CIRCUIT } from '../circuits.js';

export function buildWorld(circuit = FLOW_CIRCUIT) {
const trackPose = circuit.pose;
const TRACK_LENGTH = circuit.length, STRAIGHT_LENGTH = circuit.curveStart;
const CURVE_RADIUS = (circuit.curveEnd - circuit.curveStart) / Math.PI;

// All scenery follows the same centreline as the cars. Positive lane is right.
const ROAD_HALF_WIDTH = circuit.halfWidth;
const START = -85;
const END = TRACK_LENGTH + 230;
const CURVE_END = STRAIGHT_LENGTH + Math.PI * CURVE_RADIUS;
const smooth = value => { const u = THREE.MathUtils.clamp(value, 0, 1); return u * u * (3 - 2 * u); };
const runoffWidth = (distance, side = -1) => {
  if (circuit.id === 'legacy-u') return side < 0 ? 9 * smooth((distance - STRAIGHT_LENGTH + 65) / 85) * (1 - smooth((distance - CURVE_END - 25) / 80)) : 0;
  const middle = (STRAIGHT_LENGTH + CURVE_END) / 2;
  const start = side < 0 ? STRAIGHT_LENGTH - 65 : middle - 40;
  const end = side < 0 ? middle + 35 : CURVE_END + 70;
  return 9 * smooth((distance - start) / 75) * (1 - smooth((distance - end) / 65));
};
const barrierLane = (distance, side) => side * (ROAD_HALF_WIDTH + 3.5 + runoffWidth(distance, side));
const laneAt = (lane, distance) => typeof lane === 'function' ? lane(distance) : lane;

function randomSource(seed = 4317) {
  return () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 4294967296;
  };
}

function canvasTexture(width, height, paint, repeatX = 1, repeatY = 1) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  paint(canvas.getContext('2d'), width, height);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeatX, repeatY);
  texture.anisotropy = 8;
  return texture;
}

function ribbon(laneA, laneB, { start = START, end = END, y = 0.018, step = 2, checker = false } = {}) {
  const vertices = [], uv = [], colors = [], indices = [];
  const count = Math.ceil((end - start) / step);
  if (checker) {
    for (let i = 0; i < count; i += 1) {
      const s0 = start + (end - start) * i / count;
      const s1 = start + (end - start) * (i + 1) / count;
      const points = [trackPose(s0, laneAt(laneA, s0)), trackPose(s1, laneAt(laneA, s1)), trackPose(s0, laneAt(laneB, s0)), trackPose(s1, laneAt(laneB, s1))];
      const color = new THREE.Color(i % 2 ? 0xf5f1e5 : 0xcb323b);
      for (const point of points) {
        vertices.push(point.x, y, point.z);
        colors.push(color.r, color.g, color.b);
      }
      const n = i * 4;
      indices.push(n, n + 1, n + 2, n + 2, n + 1, n + 3);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }
  for (let i = 0; i <= count; i += 1) {
    const s = start + (end - start) * i / count;
    const a = trackPose(s, laneAt(laneA, s)), b = trackPose(s, laneAt(laneB, s));
    vertices.push(a.x, y, a.z, b.x, y, b.z);
    uv.push(0, s / 6, 1, s / 6);
    if (i < count) {
      const n = i * 2;
      indices.push(n, n + 2, n + 1, n + 1, n + 2, n + 3);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function wall(lane, y, height, start = START, end = END) {
  const positions = [], indices = [];
  const count = Math.ceil((end - start) / 3);
  for (let i = 0; i <= count; i += 1) {
    const distance = start + (end - start) * i / count;
    const pose = trackPose(distance, laneAt(lane, distance));
    positions.push(pose.x, y, pose.z, pose.x, y + height, pose.z);
    if (i < count) {
      const n = i * 2;
      indices.push(n, n + 1, n + 2, n + 1, n + 3, n + 2);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}


  const root = new THREE.Group();
  root.name = 'gt-circuit-environment';
  const random = randomSource();
  const dummy = new THREE.Object3D();
  const batches = new Map();
  const boxGeometry = new THREE.BoxGeometry(1, 1, 1);
  const cylinderGeometry = new THREE.CylinderGeometry(0.44, 0.5, 1, 8);
  const material = (color, options = {}) => new THREE.MeshStandardMaterial({ color, roughness: 0.86, envMapIntensity: .25, ...options });
  const concrete = material(0xa6aaa6, { side: THREE.DoubleSide });
  const charcoal = material(0x27333b, { metalness: 0.35 });
  const silver = material(0xb6c4c7, { roughness: 0.46, metalness: 0.6, side: THREE.DoubleSide });
  const white = material(0xf0efdf);
  const red = material(0xd63b38);
  const glass = material(0x273d4b, { roughness: 0.18, metalness: 0.78, envMapIntensity: 1 });
  const blue = material(0x22758e);
  const yellow = material(0xf3bd37);

  function batch(key, geometry, mat, x, y, z, sx, sy, sz, heading = 0, color) {
    if (!batches.has(key)) batches.set(key, { geometry, material: mat, items: [] });
    const entry = { x, y, z, sx, sy, sz, heading, color };
    batches.get(key).items.push(entry);
    return entry;
  }
  function box(key, mat, x, y, z, sx, sy, sz, heading = 0) {
    return batch(key, boxGeometry, mat, x, y, z, sx, sy, sz, heading);
  }
  function pathBox(key, mat, s, lane, y, sx, sy, sz, extraHeading = 0) {
    const p = trackPose(s, lane);
    return box(key, mat, p.x, y, p.z, sx, sy, sz, p.heading + extraHeading);
  }
  function mesh(geometry, mat, receiveShadow = true) {
    const object = new THREE.Mesh(geometry, mat);
    object.receiveShadow = receiveShadow;
    root.add(object);
    return object;
  }

  const grassTexture = canvasTexture(256, 256, (ctx, width, height) => {
    ctx.fillStyle = '#637446';
    ctx.fillRect(0, 0, width, height);
    for (let i = 0; i < 15000; i += 1) {
      ctx.fillStyle = random() > 0.5 ? 'rgba(26,53,25,.20)' : 'rgba(186,175,110,.18)';
      ctx.fillRect(random() * width, random() * height, 1 + random() * 3, 1 + random() * 4);
    }
  }, 110, 110);
  const terrainPoints = Array.from({ length: 65 }, (_, i) => trackPose(START + (END - START) * i / 64));
  const minX = Math.min(...terrainPoints.map(p => p.x)), maxX = Math.max(...terrainPoints.map(p => p.x));
  const minZ = Math.min(...terrainPoints.map(p => p.z)), maxZ = Math.max(...terrainPoints.map(p => p.z));
  const groundPadding = circuit.id === 'flow-s' ? 20000 : 1800;
  const groundWidth = maxX - minX + groundPadding, groundLength = maxZ - minZ + groundPadding;
  if (circuit.id === 'flow-s') grassTexture.repeat.set(groundWidth / 18, groundLength / 18);
  const ground = mesh(new THREE.PlaneGeometry(groundWidth, groundLength), material(0xffffff, { map: grassTexture }));
  ground.rotation.x = -Math.PI / 2;
  ground.position.set((minX + maxX) / 2, -0.04, (minZ + maxZ) / 2);
  // The flat distant backdrop stays below every road surface without depth ties.
  if (circuit.id === 'flow-s') { ground.material.depthWrite = false; ground.renderOrder = -1; }

  const asphaltTexture = canvasTexture(512, 512, (ctx, width, height) => {
    ctx.fillStyle = '#50555a';
    ctx.fillRect(0, 0, width, height);
    for (let i = 0; i < 46000; i += 1) {
      const value = 45 + Math.floor(random() * 90);
      ctx.fillStyle = `rgba(${value},${value + 2},${value + 3},${0.10 + random() * 0.12})`;
      ctx.fillRect(random() * width, random() * height, .6 + random(), .6 + random());
    }
    for (const x of circuit.id === 'legacy-u' ? [145, 191, 320, 366] : []) {
      const gradient = ctx.createLinearGradient(x - 11, 0, x + 11, 0);
      gradient.addColorStop(0, 'rgba(15,19,22,0)');
      gradient.addColorStop(0.5, 'rgba(15,19,22,.24)');
      gradient.addColorStop(1, 'rgba(15,19,22,0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(x - 11, 0, 22, height);
    }
  });
  mesh(ribbon(-ROAD_HALF_WIDTH, ROAD_HALF_WIDTH), material(0xffffff, { map: asphaltTexture, bumpMap: asphaltTexture, bumpScale: .012, roughness: .93 }));
  mesh(ribbon(-ROAD_HALF_WIDTH - 1.5, -ROAD_HALF_WIDTH, { y: 0.033, step: 2.6, checker: true }), material(0xffffff, { vertexColors: true }));
  mesh(ribbon(ROAD_HALF_WIDTH, ROAD_HALF_WIDTH + 1.5, { y: 0.033, step: 2.6, checker: true }), material(0xffffff, { vertexColors: true }));
  mesh(ribbon(-ROAD_HALF_WIDTH + .15, -ROAD_HALF_WIDTH + .32, { y: 0.041 }), white);
  mesh(ribbon(ROAD_HALF_WIDTH - .32, ROAD_HALF_WIDTH - .15, { y: 0.041 }), white);
  // A wider outside run-off leads to the energy-absorbing barrier, with the
  // debris fence and spectators behind it. Geometry is illustrative game art.
  const gravelTexture = canvasTexture(256, 256, (ctx, width, height) => {
    ctx.fillStyle = '#aa9b80'; ctx.fillRect(0, 0, width, height);
    for (let i = 0; i < 14000; i++) {
      const shade = 95 + Math.floor(random() * 92);
      ctx.fillStyle = `rgb(${shade + 18},${shade + 7},${shade - 12})`;
      ctx.fillRect(random() * width, random() * height, 1 + random() * 2, 1 + random() * 2);
    }
  }, 2, 1);
  for (const side of [-1, 1]) {
    if (circuit.id === 'legacy-u' && side > 0) continue;
    const gravel = mesh(ribbon(distance => barrierLane(distance, side) - side * 1.5, side * (ROAD_HALF_WIDTH + 1.55), {
      start: STRAIGHT_LENGTH - 65, end: CURVE_END + 135, y: .012,
    }), material(0xffffff, { map: gravelTexture, roughness: 1 }));
    gravel.name = `outside-corner-gravel-${side}`;
  }
  const rubberTrace = material(0x181b1d, { transparent: true, opacity: .22, depthWrite: false });
  const lines = circuit.id === 'legacy-u' ? [-5.4, -2.7, 0, 2.7, 5.4].map(lane => () => lane) : [circuit.racingLine];
  for (const line of lines) for (const tire of [-.72, .72]) {
    mesh(ribbon(s => line(s) + tire - .13, s => line(s) + tire + .13,
      { start: 120, end: TRACK_LENGTH - 80, y: .046 }), rubberTrace, false);
  }
  // Short edge marks and reflector posts provide fixed, nearby speed references.
  for (let s = 32; s < TRACK_LENGTH - 10; s += 12) for (const side of [-1, 1]) {
    pathBox('edge-reference-marks', white, s, side * (ROAD_HALF_WIDTH - .65), .052, .25, .012, 2.4);
  }
  // Pit apron stays outside the race surface and has its own lane line.
  const apronTexture = canvasTexture(256, 256, (ctx, width, height) => {
    ctx.fillStyle = '#8f938f'; ctx.fillRect(0, 0, width, height);
    for (let i = 0; i < 2400; i += 1) {
      ctx.fillStyle = random() > .5 ? 'rgba(250,250,244,.05)' : 'rgba(28,34,32,.07)';
      ctx.fillRect(random() * width, random() * height, 1 + random() * 3, 1 + random() * 3);
    }
    ctx.strokeStyle = 'rgba(32,38,36,.16)'; ctx.lineWidth = 4;
    for (let i = 0; i < 22; i += 1) {
      const x = random() * width, y = random() * height;
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + (random() - .5) * 96, y + (random() - .5) * 96); ctx.stroke();
    }
    ctx.fillStyle = 'rgba(26,30,28,.10)';
    for (let i = 0; i < 12; i += 1) { ctx.beginPath(); ctx.arc(random() * width, random() * height, 7 + random() * 24, 0, Math.PI * 2); ctx.fill(); }
  }, 3, 44);
  const pitApron = mesh(new THREE.PlaneGeometry(10, 218), material(0xffffff, { map: apronTexture }));
  pitApron.rotation.x = -Math.PI / 2;
  pitApron.position.set(-18, -0.005, 36);
  for (let s = -66; s < 138; s += 9) pathBox('paint', white, s, -16, 0.044, 0.16, 0.012, 4.5);

  // Two paired rows and one centred rear slot, open at the back like real grid paint.
  for (const { distance: s, lane } of STARTING_GRID) {
    pathBox('paint', white, s + 2.65, lane, 0.05, 3.45, 0.02, 0.15);
    pathBox('paint', white, s, lane - 1.72, 0.05, 0.13, 0.02, 5.4);
    pathBox('paint', white, s, lane + 1.72, 0.05, 0.13, 0.02, 5.4);
  }
  pathBox('paint', white, 2.2, 0, 0.055, ROAD_HALF_WIDTH * 2 - .5, 0.02, 0.34);
  // The first painted edge coincides with the v5 nose-based timing line.
  for (let row = 0; row < 3; row += 1) {
    for (let column = 0; column < ROAD_HALF_WIDTH * 2; column += 1) {
      pathBox((row + column) % 2 ? 'finish-white' : 'finish-dark', (row + column) % 2 ? white : charcoal,
        TRACK_LENGTH + row * 0.8 + (circuit.id === 'flow-s' ? .4 : 0), column - ROAD_HALF_WIDTH + .5, 0.05, 1, 0.02, 0.8);
    }
  }

  // Continuous curved crash barriers and a light, open catch fence.
  const fenceTexture = canvasTexture(64, 64, (ctx) => {
    ctx.strokeStyle = 'rgba(154,176,184,.57)';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    for (let v = -64; v <= 128; v += 16) {
      ctx.moveTo(v, 0); ctx.lineTo(v + 64, 64);
      ctx.moveTo(v, 0); ctx.lineTo(v - 64, 64);
    }
    ctx.stroke();
  });
  const fenceMaterial = new THREE.MeshStandardMaterial({ map: fenceTexture, transparent: true, alphaTest: 0.15, side: THREE.DoubleSide, roughness: 0.65, depthWrite: false });
  for (const side of [-1, 1]) {
    const rail = distance => barrierLane(distance, side);
    mesh(wall(rail, .04, .30), concrete);
    for (const y of [.30, .65, 1.0]) mesh(wall(rail, y, .29), silver);
    const fence = wall(distance => rail(distance) + side * .60, 1.1, 3.3);
    const uv = [];
    for (let i = 0; i < fence.attributes.position.count / 2; i += 1) uv.push(i * 2, 0, i * 2, 4);
    fence.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    mesh(fence, fenceMaterial, false);
    for (let s = START; s < END; s += 5) {
      pathBox('rail-posts', charcoal, s, rail(s) + side * .15, .72, .16, 1.44, .18);
      pathBox('fence-posts', silver, s, rail(s) + side * .65, 2.25, .13, 4.5, .13);
    }
    for (let s = START; s < END; s += 12) pathBox('rail-reflectors', white, s, rail(s) - side * .04, 1.15, .10, .16, .22);
  }
  const barrierRed = material(0xb52b31, { roughness: .66 });
  const barrierWhite = material(0xdeddd0, { roughness: .72 });
  for (let s = STRAIGHT_LENGTH - 30, index = 0; s < CURVE_END - 40; s += 2.55, index++) {
    const side = circuit.curvatureAt(s) < 0 ? 1 : -1;
    const lane = barrierLane(s, side) - side * .75;
    pathBox(index % 3 === 0 ? 'impact-modules-white' : 'impact-modules-red', index % 3 === 0 ? barrierWhite : barrierRed, s, lane, .63, 1.24, 1.26, 2.47);
    pathBox('impact-module-bindings', charcoal, s, lane + .63, .63, .028, .14, 2.43);
    for (const offset of [-.75, .75]) pathBox('impact-module-straps', charcoal, s + offset, lane + .64, .64, .028, 1.11, .065);
  }
  const tireBarrierGeometry = new THREE.TorusGeometry(.35, .115, 8, 12);
  tireBarrierGeometry.rotateX(Math.PI / 2);
  const tireBarrierMaterial = material(0x1d2327, { roughness: .96 });
  for (let s = CURVE_END - 38; s < CURVE_END + 36; s += .94) {
    for (let row = 0; row < 3; row++) {
      const side = circuit.id === 'flow-s' ? 1 : -1;
      const p = trackPose(s, barrierLane(s, side) - side * .62);
      batch('tire-barrier-stacks', tireBarrierGeometry, tireBarrierMaterial, p.x, .15 + row * .27, p.z, 1, 1, 1, p.heading);
    }
  }

  const brandTexture = canvasTexture(512, 128, (ctx, width, height) => {
    ctx.fillStyle = '#152e41'; ctx.fillRect(0, 0, width, height);
    ctx.font = 'italic 900 72px Arial, sans-serif'; ctx.textBaseline = 'middle';
    ctx.fillStyle = '#f6f5e8'; ctx.fillText('Zala', 24, height / 2 + 3);
    ctx.fillStyle = '#ffd046'; ctx.fillText('Fun', 181, height / 2 + 3);
    ctx.font = 'italic 900 63px Arial, sans-serif'; ctx.fillStyle = '#f6f5e8'; ctx.fillText('GT', 386, height / 2 + 3);
    ctx.fillStyle = '#e5b62f'; ctx.fillRect(0, height - 6, width, 6);
  });
  const brandMaterial = material(0xffffff, { map: brandTexture });
  const panelGeometry = new THREE.PlaneGeometry(1, 1);
  for (let s = -45; s < END; s += 24) {
    for (const side of [-1, 1]) {
      const p = trackPose(s, barrierLane(s, side) - side * .17);
      batch('brand-panels', panelGeometry, brandMaterial, p.x, 0.83, p.z, 7.7, 1.25, 1, p.heading - side * Math.PI / 2);
    }
  }

  // Modular pit garages, glazed hospitality deck and a restrained timing tower.
  // The facade wears racing livery: dark panels, a team colour band and a lit
  // bay number on every door instead of anonymous concrete.
  const pitFacade = material(0x232b33, { metalness: .22, roughness: .72 });
  const bayColors = [0xd63b38, 0x22758e, 0xf3bd37, 0x2f8f5b, 0xcfdae4];
  const bayNumbers = ['07', '12', '23', '36', '88'].map(number => material(0xffffff, {
    map: canvasTexture(160, 160, (ctx, width, height) => {
      ctx.fillStyle = '#141a20'; ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = '#f3bd37'; ctx.fillRect(0, height - 13, width, 13);
      ctx.fillStyle = '#f6f5e8'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = 'italic 900 100px Arial, sans-serif'; ctx.fillText(number, width / 2, height / 2 - 6);
    }),
  }));
  box('pit-concrete', pitFacade, -30, 4.1, 26, 16, 8.2, 154);
  box('pit-glazing', glass, -21.93, 5.9, 26, 0.12, 2.5, 147);
  box('pit-roof', white, -28.9, 8.5, 26, 20, 0.45, 159);
  box('pit-balcony', charcoal, -20, 4.12, 26, 4.5, 0.3, 154);
  box('pit-rail', silver, -18.04, 4.85, 26, 0.1, 1.2, 153);
  for (let s = -47; s < 103; s += 3) {
    box('window-mullions', charcoal, -21.82, 5.9, s, .08, 2.5, .055);
    box('balcony-pickets', silver, -18.02, 4.8, s, .045, 1.2, .045);
  }
  for (let s = -47; s < 103; s += 5.5) box('pit-lights', white, -20.7, 4.0, s, .34, .07, .5);
  for (let s = -43; s <= 100; s += 11) {
    const bay = Math.round((s + 43) / 11) % 5;
    box('garage-doors', charcoal, -21.91, 1.8, s, 0.13, 3.25, 8.2);
    box('pit-pillars', pitFacade, -20.3, 3.9, s + 5.4, 1, 7.8, 0.55);
    box('pit-pillar-edges', yellow, -19.78, 3.9, s + 5.4, .06, 7.6, .12);
    box('garage-header', yellow, -21.65, 3.61, s, 0.15, 0.3, 8.7);
    box('pit-team-bands', pitFacade, -21.6, 3.98, s, .16, .5, 8.7, 0, bayColors[bay]);
    batch('pit-bay-numbers', panelGeometry, bayNumbers[bay], -21.72, 2.35, s, 1.9, 1.9, 1, Math.PI / 2);
    box('garage-interior', concrete, -21.80, 2.9, s, .05, .11, 7.4);
    for (const offset of [-3.6, 3.6]) {
      box('garage-pillars', silver, -21.65, 1.7, s + offset, .15, 3.2, .13);
      box('pit-tool-carts', charcoal, -21.1, .45, s + offset, .65, .9, .8);
      for (let tire = 0; tire < 3; tire++) batch('pit-tires', cylinderGeometry, charcoal, -21.0, .18 + tire * .30, s + offset + 1, .65, .29, .65);
    }
    const p = trackPose(s, -18.8);
    batch('pit-brand', panelGeometry, brandMaterial, p.x, 5.3, p.z, 7, 1.7, 1, Math.PI / 2);
  }
  box('pit-concrete', concrete, -28, 9.2, 124, 13, 18.4, 12);
  box('pit-glazing', glass, -27, 15.4, 124, 15, 3, 14);
  box('pit-roof', white, -27, 17.1, 124, 17, 0.6, 16);

  // Working pit apron: painted pit boxes, a tyre wall, pit boards and a
  // service truck so the slab in front of the garages reads as a live pit
  // lane instead of empty concrete. Geometry stays instanced.
  const boardTexture = canvasTexture(256, 160, (ctx, width, height) => {
    ctx.fillStyle = '#141b22'; ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#f3bd37'; ctx.fillRect(0, 0, width, 9); ctx.fillRect(0, height - 9, width, 9);
    ctx.textBaseline = 'middle';
    ctx.font = 'italic 900 72px Arial, sans-serif'; ctx.fillStyle = '#f6f5e8'; ctx.fillText('GT', 22, height / 2 - 12);
    ctx.font = '900 26px Arial, sans-serif'; ctx.fillStyle = '#f3bd37'; ctx.fillText('PIT 23', 148, height / 2 + 20);
  });
  const boardMaterial = material(0xffffff, { map: boardTexture, side: THREE.DoubleSide });
  const stripeMaterial = material(0xffffff, { map: canvasTexture(128, 64, (ctx, width, height) => {
    ctx.fillStyle = '#141b22'; ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#f3bd37';
    for (let x = -height; x < width; x += 32) { ctx.beginPath(); ctx.moveTo(x, height); ctx.lineTo(x + height, 0); ctx.lineTo(x + height + 16, 0); ctx.lineTo(x + 16, height); ctx.fill(); }
  }) });
  const boxMarkTexture = canvasTexture(256, 256, (ctx, width, height) => {
    ctx.clearRect(0, 0, width, height);
    ctx.strokeStyle = 'rgba(243,189,55,.95)'; ctx.lineWidth = 14;
    ctx.strokeRect(17, 17, width - 34, height - 34);
    ctx.fillStyle = 'rgba(243,189,55,.20)'; ctx.fillRect(17, 17, width - 34, height - 34);
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = 'italic 900 124px Arial, sans-serif'; ctx.fillStyle = 'rgba(246,245,232,.72)'; ctx.fillText('GT', width / 2, height / 2 - 14);
    ctx.font = '900 36px Arial, sans-serif'; ctx.fillStyle = 'rgba(243,189,55,.90)'; ctx.fillText('PIT BOX', width / 2, height / 2 + 76);
  });
  const boxMarkMaterial = material(0xffffff, { map: boxMarkTexture, transparent: true, depthWrite: false, roughness: .95 });
  for (const bay of [-54, -43, -32, -21, -10]) {
    for (const lane of [-14.4, -21.4]) box('pit-box-paint', yellow, lane, .045, bay + 5.4, .14, .02, 10.2);
    box('pit-box-paint', yellow, -17.9, .045, bay + 10.3, 7.1, .02, .14);
  }
  for (const bay of [-43, -32]) {
    const mark = mesh(new THREE.PlaneGeometry(6.4, 6.4), boxMarkMaterial, false);
    mark.rotation.x = -Math.PI / 2; mark.position.set(-17.9, .034, bay + 5.4);
  }
  for (const [x, z] of [[-20.4, -33.5], [-20.4, -44.5], [-20.4, -55.5], [-15.2, -49], [-15.2, -61], [-16.6, -31.5], [-16.4, -18.5]]) {
    for (let tire = 0; tire < 5; tire += 1) batch('pit-tyre-stacks', cylinderGeometry, charcoal, x, .16 + tire * .30, z, .62, .30, .62);
    batch('pit-tyre-bands', cylinderGeometry, silver, x, .46, z, .64, .06, .64);
  }
  for (const [x, z] of [[-15.1, -28], [-15.1, -44]]) {
    batch('pit-board-poles', boxGeometry, charcoal, x, .85, z, .07, 1.7, .07);
    batch('pit-board-panels', panelGeometry, boardMaterial, x, 1.62, z, 1.15, .72, 1, Math.PI / 2);
  }
  batch('pit-truck', boxGeometry, white, -19.5, 1.36, -53, 2.5, 2.24, 6.4);
  batch('pit-truck', boxGeometry, white, -19.5, 1.02, -48.4, 2.36, 1.56, 2.6);
  batch('pit-truck-glass', boxGeometry, glass, -19.5, 1.62, -47.2, 2.06, .66, .14);
  for (const [x, z] of [[-20.6, -51], [-18.4, -51], [-20.6, -56], [-18.4, -56]]) batch('pit-truck-wheels', boxGeometry, charcoal, x, .34, z, .34, .68, 1.05);
  box('pit-wall', concrete, -13.5, .56, 15, .34, 1.12, 54);
  box('pit-wall-cap', yellow, -13.5, 1.16, 15, .44, .1, 54);
  for (let s = -10; s < 40; s += 12) batch('pit-wall-panels', panelGeometry, brandMaterial, -13.29, .62, s + 5, 6.2, .74, 1, Math.PI / 2);
  for (let s = -4; s < 40; s += 12) batch('pit-wall-checkers', panelGeometry, stripeMaterial, -13.29, .62, s + 5, 3.6, .74, 1, Math.PI / 2);
  box('pit-guide', yellow, -13.95, .045, -14, .16, .02, 112);

  const startLamps = [];
  const lensTexture = canvasTexture(64, 64, (ctx) => {
    ctx.fillStyle = '#97121d'; ctx.fillRect(0, 0, 64, 64);
    ctx.fillStyle = '#fff0e8';
    for (let y = 4; y < 64; y += 7) for (let x = 4; x < 64; x += 7) {
      ctx.beginPath(); ctx.arc(x, y, 1.6, 0, Math.PI * 2); ctx.fill();
    }
  });
  const checkerTexture = canvasTexture(256, 128, (ctx) => {
    ctx.fillStyle = '#f9f7e9'; ctx.fillRect(0, 0, 256, 128);
    ctx.fillStyle = '#233442';
    for (let y = 0; y < 4; y += 1) for (let x = 0; x < 8; x += 1) if ((x + y) % 2) ctx.fillRect(x * 32, y * 32, 32, 32);
  });
  const checkerMaterial = material(0xffffff, { map: checkerTexture, side: THREE.DoubleSide });
  function gantry(s, isStart) {
    pathBox('gantry-metal', charcoal, s, -12.5, 4.8, 0.64, 9.6, 0.64);
    pathBox('gantry-metal', charcoal, s, 12.5, 4.8, 0.64, 9.6, 0.64);
    pathBox('gantry-metal', charcoal, s, 0, 8.85, 26, 0.45, 1.3);
    pathBox('gantry-metal', charcoal, s, 0, 7.5, 26, 0.28, 1.3);
    for (let lane = -12; lane < 13; lane += 3) pathBox('gantry-struts', silver, s, lane, 8.2, 0.14, 1.5, 0.2);
    const p = trackPose(s - 0.72, 0);
    batch('gantry-brand', panelGeometry, brandMaterial, p.x, 8.25, p.z, 15.2, 1.8, 1, p.heading + Math.PI);
    if (!isStart) {
      for (const lane of [-10, 10]) {
        const q = trackPose(s - 0.74, lane);
        batch('checker-panels', panelGeometry, checkerMaterial, q.x, 8.25, q.z, 3.6, 1.8, 1, q.heading + Math.PI);
      }
      return;
    }
    pathBox('gantry-metal', charcoal, s - 0.2, 0, 6.3, 12.5, 2.2, 0.66);
    for (let i = 0; i < 5; i += 1) {
      const q = trackPose(s - 0.58, (i - 2) * 2.35);
      const housing = new THREE.Mesh(new THREE.CylinderGeometry(0.84, 0.84, 0.18, 20), charcoal);
      housing.rotation.x = Math.PI / 2;
      housing.position.set(q.x, 6.3, q.z);
      root.add(housing);
      const lampMaterial = new THREE.MeshBasicMaterial({ color: 0x321015, map: lensTexture, toneMapped: false });
      const lamp = new THREE.Mesh(new THREE.CircleGeometry(0.67, 24), lampMaterial);
      lamp.position.set(q.x, 6.3, q.z - 0.11);
      lamp.rotation.y = Math.PI;
      root.add(lamp);
      startLamps.push(lamp);
    }
  }
  gantry(16, true);
  gantry(TRACK_LENGTH + 3, false);

  const crowdTexture = new THREE.TextureLoader().load('assets/circuit-spectators.webp');
  crowdTexture.colorSpace = THREE.SRGBColorSpace;
  crowdTexture.anisotropy = 4;
  const crowdMaterial = new THREE.MeshBasicMaterial({ map: crowdTexture, alphaTest: .52, side: THREE.DoubleSide, color: 0xd2d7db });
  const crowdGeometries = Array.from({ length: 6 }, (_, variant) => {
    const geometry = new THREE.PlaneGeometry(1, 1), uv = geometry.attributes.uv;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, (variant % 3 + uv.getX(i)) / 3, (1 - Math.floor(variant / 3) + uv.getY(i)) / 2);
    return geometry;
  });
  function spectator(s, lane, height, side) {
    const p = trackPose(s, lane);
    const scale = 1.90 + random() * .32;
    const heading = p.heading + side * Math.PI / 2;
    const variant = Math.floor(random() * 6);
    const person = batch(`crowd-${variant}`, crowdGeometries[variant], crowdMaterial, p.x, height + scale * .49, p.z, scale, scale, 1, heading, new THREE.Color().setRGB(.80 + random() * .20, .80 + random() * .20, .80 + random() * .20));
    person.phase = random() * Math.PI * 2;
  }
  function grandstand(s, side, length, roof = false) {
    const setback = Math.max(runoffWidth(s - length / 2, side), runoffWidth(s, side), runoffWidth(s + length / 2, side));
    for (let row = 0; row < 6; row += 1) {
      const lane = side * (20 + setback + row * 1.18);
      const y = 0.55 + row * 0.62;
      for (let offset = -length / 2 + 1; offset < length / 2; offset += 2) {
        pathBox('stand-steps', concrete, s + offset, lane, y / 2, 1.24, y, 2.12);
        pathBox(row % 2 ? 'stand-seats-blue' : 'stand-seats-red', row % 2 ? blue : red, s + offset, lane, y + 0.12, 0.8, 0.21, 2.12);
      }
      for (let offset = -length / 2 + 1.5; offset < length / 2 - 1; offset += 0.95) {
        if (random() > 0.06) spectator(s + offset + (random() - 0.5) * 0.15, lane, y + 0.12, side);
      }
    }
    for (let offset = -length / 2 + 1; offset < length / 2; offset += 2) pathBox('stand-front', white, s + offset, side * (18.5 + setback), 0.75, 0.36, 1.5, 2.12);
    if (roof) {
      pathBox('stand-roof', white, s, side * (23.6 + setback), 7.2, 12, 0.3, length + 3);
      for (let offset = -length / 2; offset <= length / 2; offset += 13) {
        pathBox('stand-roof-posts', silver, s + offset, side * (28.1 + setback), 3.6, 0.26, 7.2, 0.26);
      }
    }
  }
  grandstand(23, 1, 69, true);
  grandstand(110, 1, 65, true);
  grandstand(208, -1, 54);
  grandstand(STRAIGHT_LENGTH + Math.PI * CURVE_RADIUS / 2, -1, 50);
  grandstand(STRAIGHT_LENGTH + Math.PI * CURVE_RADIUS / 2 + 64, -1, 66, true);
  grandstand(TRACK_LENGTH - 95, -1, 65, true);
  grandstand(TRACK_LENGTH - 15, -1, 67);
  grandstand(TRACK_LENGTH - 30, 1, 65);

  // Distance boards, track flags and trees repeat along the racing line for parallax.
  for (const [s, label] of [[STRAIGHT_LENGTH - 150, '150'], [STRAIGHT_LENGTH - 100, '100'], [STRAIGHT_LENGTH - 50, '50'], [TRACK_LENGTH - 150, '150'], [TRACK_LENGTH - 100, '100'], [TRACK_LENGTH - 50, '50']]) {
    const map = canvasTexture(128, 128, (ctx) => {
      ctx.fillStyle = '#f3f0df'; ctx.fillRect(0, 0, 128, 128);
      ctx.fillStyle = '#213849'; ctx.font = '900 60px Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(label, 64, 67);
      ctx.strokeStyle = '#213849'; ctx.lineWidth = 6; ctx.strokeRect(4, 4, 120, 120);
    });
    const markerLane = barrierLane(s, -1) + .35;
    const p = trackPose(s, markerLane);
    const board = mesh(new THREE.PlaneGeometry(1.8, 1.8), material(0xffffff, { map }), false);
    board.position.set(p.x, 2.5, p.z);
    board.rotation.y = p.heading + Math.PI;
    pathBox('marker-posts', silver, s, markerLane, 1.35, 0.12, 2.7, 0.12);
  }
  const flagGeometry = new THREE.PlaneGeometry(1, 1, 5, 2);
  const flagRed = material(0xd63b38, { side: THREE.DoubleSide });
  const flagBlue = material(0x22758e, { side: THREE.DoubleSide });
  for (let s = -38; s < END - 40; s += 35) {
    // Keep the elevated S camera's approach and exit sightlines clear.
    if (circuit.id === 'flow-s' && s > STRAIGHT_LENGTH - 200 && s < CURVE_END + 120) continue;
    const side = circuit.id === 'flow-s' ? 1 : circuit.curvatureAt(s) > 0 ? -1 : 1;
    const p = trackPose(s, barrierLane(s, side) + side * 4.6);
    box('flag-poles', silver, p.x, 3.8, p.z, 0.09, 7.6, 0.09);
    const mat = s % 3 < 1 ? checkerMaterial : (s % 2 < 1 ? flagRed : flagBlue);
    const key = mat === checkerMaterial ? 'flags-checkered' : mat === flagRed ? 'flags-red' : 'flags-blue';
    const heading = p.heading + 0.7;
    batch(key, flagGeometry, mat, p.x + Math.cos(heading) * .85, 6.7, p.z - Math.sin(heading) * .85, 1.7, 1.1, 1, heading);
  }
  const treeTexture = new THREE.TextureLoader().load('assets/circuit-oak.webp');
  treeTexture.colorSpace = THREE.SRGBColorSpace;
  treeTexture.anisotropy = 4;
  const foliage = new THREE.MeshBasicMaterial({ map: treeTexture, alphaTest: .50, side: THREE.DoubleSide, color: 0xd8ddd0 });
  for (let s = -120; s < END + 70; s += 13) {
    for (const side of [-1, 1]) {
      const lane = side * (39 + random() * 31);
      // Keep the pit facade and crowd silhouettes unobstructed.
      if (side < 0 && s < 147 && s > -63) continue;
      const p = trackPose(s + random() * 8, lane);
      const height = 9 + random() * 8;
      batch('tree-crowns', panelGeometry, foliage, p.x, height * .425, p.z, height * (0.60 + random() * .11), height, 1, 0, new THREE.Color().setRGB(.83 + random() * .17, .86 + random() * .14, .78 + random() * .18));
    }
  }
  for (let s = CURVE_END + 115; s < TRACK_LENGTH - 170; s += 16) for (const side of [-1, 1]) {
    const p = trackPose(s + random() * 3, side * (19.5 + random() * 2));
    const height = 8.5 + random() * 3;
    batch('tree-crowns', panelGeometry, foliage, p.x, height * .425, p.z, height * .62, height, 1, 0, new THREE.Color(0xc8d3c2));
  }

  const shadowCasters = new Set(['pit-concrete', 'pit-roof', 'pit-pillars', 'stand-roof', 'stand-roof-posts', 'tree-trunks', 'tree-crowns', 'gantry-metal', 'gantry-struts', 'rail-posts', 'fence-posts', 'pit-wall', 'pit-wall-cap', 'pit-tyre-stacks', 'pit-board-poles', 'pit-board-panels', 'pit-truck']);
  for (const [key, entry] of batches) {
    const object = new THREE.InstancedMesh(entry.geometry, entry.material, entry.items.length);
    object.name = key;
    object.castShadow = shadowCasters.has(key);
    object.receiveShadow = true;
    entry.items.forEach((item, index) => {
      dummy.position.set(item.x, item.y, item.z);
      dummy.rotation.set(0, item.heading, 0);
      dummy.scale.set(item.sx, item.sy, item.sz);
      dummy.updateMatrix();
      object.setMatrixAt(index, dummy.matrix);
      if (item.color !== undefined) object.setColorAt(index, item.color instanceof THREE.Color ? item.color : new THREE.Color(item.color));
    });
    if (key.startsWith('crowd-') || key === 'tree-crowns') {
      object.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      entry.mesh = object;
    }
    object.computeBoundingSphere();
    entry.mesh = object;
    root.add(object);
  }
  let previousTick = -1;
  function update(time, celebrationIntensity = 0, cameraPosition = null) {
    const cheer = THREE.MathUtils.clamp(celebrationIntensity, 0, 1);
    if (cameraPosition) {
      const trees = batches.get('tree-crowns');
      trees.items.forEach((tree, index) => {
        dummy.position.set(tree.x, tree.y, tree.z);
        dummy.rotation.set(0, Math.atan2(cameraPosition.x - tree.x, cameraPosition.z - tree.z), 0);
        dummy.scale.set(tree.sx, tree.sy, 1); dummy.updateMatrix();
        trees.mesh.setMatrixAt(index, dummy.matrix);
      });
      trees.mesh.instanceMatrix.needsUpdate = true;
    }
    const flagPosition = flagGeometry.attributes.position;
    for (let i = 0; i < flagPosition.count; i += 1) {
      const x = flagPosition.getX(i);
      flagPosition.setZ(i, Math.sin(x * 7 - time * 5) * 0.12 * (x + 0.5));
    }
    flagPosition.needsUpdate = true;
    const tick = Math.floor(time * 18);
    if (tick === previousTick) return;
    previousTick = tick;
    for (let variant = 0; variant < 6; variant++) {
      const spectators = batches.get(`crowd-${variant}`);
      spectators.items.forEach((person, index) => {
        const sway = Math.sin(time * (cheer ? 6 : 1.7) + person.phase);
        dummy.position.set(person.x, person.y + Math.max(0, sway) * cheer * .09, person.z);
        dummy.rotation.set(0, cameraPosition ? Math.atan2(cameraPosition.x - person.x, cameraPosition.z - person.z) : person.heading, sway * (.006 + cheer * .026));
        dummy.scale.set(person.sx, person.sy, 1); dummy.updateMatrix();
        spectators.mesh.setMatrixAt(index, dummy.matrix);
      });
      spectators.mesh.instanceMatrix.needsUpdate = true;
    }
  }
  function dispose() {
    const geometries = new Set(), materials = new Set(), textures = new Set();
    root.traverse(object => {
      if (object.geometry) geometries.add(object.geometry);
      for (const mat of Array.isArray(object.material) ? object.material : object.material ? [object.material] : []) {
        materials.add(mat);
        for (const value of Object.values(mat)) if (value?.isTexture) textures.add(value);
      }
      if (object.isInstancedMesh) object.dispose();
    });
    geometries.forEach(item => item.dispose()); materials.forEach(item => item.dispose()); textures.forEach(item => item.dispose());
  }
  return { root, startLamps, update, dispose };
}
